import React, { useState } from 'react';
import { AlertCircle, Ban, CheckCircle } from 'lucide-react';
import styles from './ManageTickets.module.css';
import { BARCODE_STATUS_OPTIONS } from '../../utils/noBarcodeRules';

export type BarcodeStatusValue = (typeof BARCODE_STATUS_OPTIONS)[number]['value'];

type Props = {
  value: BarcodeStatusValue | 'Mixed';
  onSelect: (status: BarcodeStatusValue) => void;
};

const StatusGlyph: React.FC<{ status: string; size: number }> = ({ status, size }) => {
  if (status === 'Null') return <Ban size={size} className={styles.barcodeStatusNull} aria-hidden />;
  if (status === 'Barcode Available') return <CheckCircle size={size} className={styles.barcodeStatusOk} aria-hidden />;
  return <AlertCircle size={size} className={styles.barcodeStatusMissing} aria-hidden />;
};

const BarcodeStatusDropdown: React.FC<Props> = ({ value, onSelect }) => {
  const [open, setOpen] = useState(false);
  const currentLabel = BARCODE_STATUS_OPTIONS.find((option) => option.value === value)?.label || value;

  return (
    <div
      className={`${styles.statusDropdownWrapper} ${open ? styles.statusDropdownOpen : ''}`}
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className={styles.barcodeStatusButton}
        title={`Barcode status: ${currentLabel}`}
        aria-label={`Barcode status: ${currentLabel}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((previous) => !previous)}
      >
        <StatusGlyph status={value} size={14} />
      </button>
      {open && (
        <div className={styles.statusDropdownList} role="menu" aria-label="Barcode status">
          {BARCODE_STATUS_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              role="menuitemradio"
              aria-checked={value === option.value}
              className={`${styles.statusDropdownListItem} ${styles.barcodeStatusOption}`}
              onClick={() => {
                setOpen(false);
                onSelect(option.value);
              }}
            >
              <StatusGlyph status={option.value} size={14} />
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default BarcodeStatusDropdown;